import { type ClassValue, clsx } from 'clsx';
import { format as formatDate, formatDistanceToNow } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import { getLanguage, t } from '@/i18n';
import { dateFnsLocaleFor, datePatternsFor, numberLocaleFor } from '@/i18n/format';
import { InspectionStatus } from '../common/inspection';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function getUploadedFileUrl(fileLocation: string) {
	const baseUrl = import.meta.env.VITE_FILE_SERVER_URL || 'http://localhost:3000';
	if (fileLocation.startsWith('http://') || fileLocation.startsWith('https://')) {
		return fileLocation;
	}
	if (fileLocation.startsWith('/')) {
		return `${baseUrl}${fileLocation}`;
	}
	return `${baseUrl}/${fileLocation}`;
}

export function getDateFnsLocale() {
	return dateFnsLocaleFor(getLanguage());
}

// The date and currency formatters read the interface language when called. Their output follows a
// language switch only when the component showing it re-renders (useTranslation, or a parent that does);
// a memoized formatted value keeps the old format until then.
export function formatDateTime(dateString: string | Date | number, format?: string): string {
	if (!dateString) return String(dateString);
	const language = getLanguage();
	return formatDate(dateString, format ?? datePatternsFor(language).dateTime, { locale: dateFnsLocaleFor(language) });
}

export function formatDateOnly(dateString: string) {
	if (!dateString) return dateString;
	const language = getLanguage();
	return formatDate(new Date(dateString), datePatternsFor(language).date, { locale: dateFnsLocaleFor(language) });
}

export function formatDateDistanceFromNow(dateString: string) {
	if (!dateString) return dateString;
	return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: getDateFnsLocale() });
}

export function getInspectionStatusLabel(status: InspectionStatus) {
	switch (status) {
		case InspectionStatus.COMPLETED:
			return t('common.status.completed');
		case InspectionStatus.IN_PROGRESS:
			return t('common.status.inProgress');
		case InspectionStatus.SCHEDULED:
			return t('common.status.scheduled');
		default:
			return t('common.states.unknown');
	}
}

export const formatCurrency = (amount: string | number | null | undefined) => {
	amount = amount ? Number(amount) : 0;
	return new Intl.NumberFormat(numberLocaleFor(getLanguage()), {
		style: 'currency',
		currency: 'EUR',
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	}).format(amount);
};

export const formatCurrencyWithCents = (amount: string | number | null | undefined) => {
	amount = amount ? Number(amount) / 100 : 0;
	return new Intl.NumberFormat(numberLocaleFor(getLanguage()), {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};
